import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { userService } from '../services/userService'
import { User, UserFormData } from '../types/user'
import { UserForm } from '../components/UserForm'
import { UserList } from '../components/UserList'

export function SettingsUsers() {
  const { hasPermission } = useAuth()
  
  // Check if user has permission to view users
  if (!hasPermission('users:read') && !hasPermission('*')) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Access Denied</h2>
          <p>You don't have permission to view users.</p>
        </div>
      </div>
    )
  }
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: UserFormData) => userService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserFormData> }) =>
      userService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const activateMutation = useMutation({
    mutationFn: (id: string) => userService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => userService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userService.resetPassword(id, password),
  })

  const enable2faMutation = useMutation({
    mutationFn: (id: string) => userService.enable2fa(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const disable2faMutation = useMutation({
    mutationFn: (id: string) => userService.disable2fa(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const handleSubmit = async (data: UserFormData) => {
    if (editingUser) {
      await updateMutation.mutateAsync({ id: editingUser.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await deleteMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to delete user')
      }
    }
  }

  const handleActivate = async (id: string) => {
    try {
      await activateMutation.mutateAsync(id)
    } catch (error) {
      alert('Failed to activate user')
    }
  }

  const handleDeactivate = async (id: string) => {
    if (window.confirm('Are you sure you want to deactivate this user? They will not be able to log in.')) {
      try {
        await deactivateMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to deactivate user')
      }
    }
  }

  const handleResetPassword = async (id: string) => {
    const newPassword = prompt('Enter new password (minimum 8 characters):')
    if (newPassword && newPassword.length >= 8) {
      try {
        await resetPasswordMutation.mutateAsync({ id, password: newPassword })
        alert('Password reset successfully')
      } catch (error) {
        alert('Failed to reset password')
      }
    } else if (newPassword) {
      alert('Password must be at least 8 characters long')
    }
  }

  const handleEnable2fa = async (id: string) => {
    try {
      await enable2faMutation.mutateAsync(id)
    } catch (error) {
      alert('Failed to enable 2FA')
    }
  }

  const handleDisable2fa = async (id: string) => {
    if (window.confirm('Disable 2FA for this user? They will be able to log in with password only.')) {
      try {
        await disable2faMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to disable 2FA')
      }
    }
  }

  if (showAddForm || editingUser) {
    return (
      <div className="settings-content" role="main">
        <div className="settings-header">
          <h1>{editingUser ? 'Edit User' : 'Add New User'}</h1>
          <button
            className="btn-secondary"
            onClick={() => {
              setShowAddForm(false)
              setEditingUser(null)
            }}
          >
            Back to Users
          </button>
        </div>
        {(createMutation.isError || updateMutation.isError) && (
          <div className="error-message" role="alert">
            Failed to save user. Please try again.
          </div>
        )}
        <UserForm
          initialData={editingUser || undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowAddForm(false)
            setEditingUser(null)
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </div>
    )
  }

  return (
    <div className="settings-content" role="main">
      <div className="settings-header">
        <div>
          <h1>Users</h1>
          <p className="settings-subtitle">Manage system users and their permissions</p>
        </div>
        {hasPermission('users:write') && (
          <button
            className="btn-primary"
            onClick={() => setShowAddForm(true)}
            aria-label="Add new user"
          >
            + Add User
          </button>
        )}
      </div>

      {isLoading && (
        <div className="loading" role="status" aria-live="polite">
          Loading users...
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="assertive">
          Error loading users
        </div>
      )}

      {!isLoading && !error && users && (
        <UserList
          users={users}
          onEdit={setEditingUser}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onResetPassword={handleResetPassword}
          onEnable2fa={handleEnable2fa}
          onDisable2fa={handleDisable2fa}
        />
      )}

      <div className="settings-info">
        <h2>User Management</h2>
        <div className="info-sections">
          <div className="info-section">
            <h3>Roles</h3>
            <ul>
              <li><strong>Admin:</strong> Full system access</li>
              <li><strong>Doctor:</strong> Full clinical access to patients, appointments, templates, and lab results</li>
              <li><strong>Nurse:</strong> Full clinical access to patients, appointments, templates, and lab results</li>
              <li><strong>Receptionist:</strong> Patient and appointment management</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>User Status</h3>
            <ul>
              <li><strong>Active:</strong> User can log in and access the system</li>
              <li><strong>Inactive:</strong> User cannot log in</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>2FA (Email Verification)</h3>
            <ul>
              <li><strong>Default:</strong> New users have 2FA enabled by default</li>
              <li><strong>Enabled:</strong> User enters password and email verification code</li>
              <li><strong>Disabled:</strong> User can sign in with password only</li>
              <li><strong>Manage:</strong> Change 2FA from Edit User or per-user Actions</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>2FA FAQ</h3>
            <ul>
              <li><strong>Code not received?</strong> Check spam/junk and confirm account email is correct.</li>
              <li><strong>Can users opt out?</strong> Admins can disable 2FA on user accounts if policy allows.</li>
              <li><strong>Trusted devices?</strong> Users can trust a device for 30 days during login verification.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

















